import React from 'react';
import {Drawer, Space, Tag, Timeline, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {
    CustomerComplaintDetail,
    ReviewStatus,
    ReviewTaskLog,
} from '../graphql/types/ReviewTaskType';

const {Text} = Typography;

const STATUS_COLOR: Record<string, string> = {
    [ReviewStatus.PENDING_REVIEW]: 'orange',
    [ReviewStatus.APPROVED]: 'green',
    [ReviewStatus.REJECTED]: 'red',
    [ReviewStatus.CANCELLED]: 'default',
};

/**
 * 客诉下发节点的操作人 i18n key：
 *   - EMAIL / PHONE 渠道：客服录入，归类为 customerService
 *   - OTHER 或缺失：系统自动入库（POD 质检/轨迹监控等），归类为 system
 */
const resolveComplaintSourceOperatorKey = (source?: string): string => {
    if (source === 'EMAIL' || source === 'PHONE') {
        return 'reviewTask.timeline.operator.customerService';
    }
    return 'reviewTask.timeline.operator.system';
};

interface ReviewTaskTimelineDrawerProps {
    open: boolean;
    onClose: () => void;
    /** 客诉信息（用于头部"客诉下发"虚拟节点） */
    complaint?: CustomerComplaintDetail;
    /** 审核单 log 列表 */
    logs: ReviewTaskLog[];
}

/**
 * 操作日志二级抽屉（窄）
 *
 * 生命周期轨迹：客诉下发 → DSP 提交申诉 → 审核动作
 * - 客诉下发节点由 customer_complaint 拼接（虚拟）
 * - 其余节点来自 review_task_log
 */
const ReviewTaskTimelineDrawer: React.FC<ReviewTaskTimelineDrawerProps> = ({
    open,
    onClose,
    complaint,
    logs,
}) => {
    const {t} = useTranslation();

    return (
        <Drawer
            title={
                <span className="text-base font-medium">
                    {t('reviewTask.detail.timeline')}
                </span>
            }
            placement="right"
            width={600}
            open={open}
            onClose={onClose}
            destroyOnClose
            // 浮在一级详情抽屉之上
            zIndex={1100}
            styles={{
                body: {padding: '16px 24px'},
                header: {borderBottom: '1px solid #f0f0f0'},
            }}
        >
            <Timeline
                items={[
                    // 虚拟节点：客诉下发（由 customer_complaint 拼接）
                    ...(complaint
                        ? [{
                            color: 'blue',
                            children: (
                                <div className="text-sm">
                                    <Space>
                                        <Tag color="blue">
                                            {t('reviewTask.action.COMPLAINT_CREATED')}
                                        </Tag>
                                        <Text type="secondary">
                                            {formatEpochToDateTime(complaint.createdAt)}
                                        </Text>
                                    </Space>
                                    <div className="mt-1" style={{color: '#666'}}>
                                        {t('reviewTask.detail.operatorId')}:{' '}
                                        {t(resolveComplaintSourceOperatorKey(complaint.source))}
                                    </div>
                                    {complaint.externalRefCode && (
                                        <div className="mt-1" style={{color: '#666'}}>
                                            {t('reviewTask.detail.externalRefCode')}:{' '}
                                            {complaint.externalRefCode}
                                        </div>
                                    )}
                                </div>
                            ),
                        }]
                        : []),
                    // 审核单生命周期 log
                    ...logs.map((log) => ({
                        color: STATUS_COLOR[log.toStatus] ?? 'gray',
                        children: (
                            <div className="text-sm">
                                <Space>
                                    <Tag color={STATUS_COLOR[log.toStatus] ?? 'default'}>
                                        {t(`reviewTask.action.${log.action}`)}
                                    </Tag>
                                    <Text type="secondary">{formatEpochToDateTime(log.createdAt)}</Text>
                                </Space>
                                <div className="mt-1" style={{color: '#666'}}>
                                    {t('reviewTask.detail.operatorId')}:{' '}
                                    {log.operatorName || log.operatorId}
                                    {log.fromStatus && (
                                        <>
                                            {' · '}
                                            {t(`reviewTask.status.${log.fromStatus}`, {defaultValue: log.fromStatus}) as string}
                                            {' → '}
                                            {t(`reviewTask.status.${log.toStatus}`, {defaultValue: log.toStatus}) as string}
                                        </>
                                    )}
                                </div>
                            </div>
                        ),
                    })),
                ]}
            />
        </Drawer>
    );
};

export default ReviewTaskTimelineDrawer;
